import React from "react";
import { Box, Typography } from "@mui/material";
import type { SequenceView } from "@services/drybulkCargoOperationsApi";
import { useTranslation } from "react-i18next";
import { DRYBULK_CARGO_OPS_LANG_BASE } from "../constants";
import { formatInstant } from "../utils/formatInstant";
import { InfoRow, InfoValue } from "./InfoRow";

const L = `${DRYBULK_CARGO_OPS_LANG_BASE}.sequences`;

interface CardField {
  id: string;
  label: string;
  value: React.ReactNode;
  span?: boolean;
}

const text = (value: string | number | null | undefined): string =>
  value === null || value === undefined || value === "" ? "-" : String(value);

const transporterText = (sequence: SequenceView): string =>
  sequence.transporterCompanyCode && sequence.transporterCompanyName
    ? `${sequence.transporterCompanyCode} - ${sequence.transporterCompanyName}`
    : text(sequence.transporterCompanyCode ?? sequence.transporterCompanyName);

const SequenceDetailsCard: React.FC<{
  sequence: SequenceView;
  onOpenShipper: (code: string) => void;
}> = ({ sequence, onOpenShipper }) => {
  const { t } = useTranslation();
  const detailLabel = (key: string) => t(`${L}.sequence-detail.${key}`);
  const executionLabel = (key: string) => t(`${L}.execution.section.${key}`);
  const has = (key: keyof SequenceView) => key in sequence;

  const fields: (CardField | null)[] = [
    {
      id: "planned-quantity",
      label: detailLabel("planned-quantity"),
      value: text(sequence.plannedQuantity),
      span: true,
    },
    has("actualQuantity")
      ? { id: "actual", label: executionLabel("actual"), value: text(sequence.actualQuantity) }
      : null,
    has("remainingQuantity")
      ? {
          id: "remaining",
          label: executionLabel("remaining"),
          value: text(sequence.remainingQuantity),
        }
      : null,
    { id: "hold", label: detailLabel("hold"), value: text(sequence.hold) },
    { id: "grade", label: detailLabel("cargo-type-code"), value: text(sequence.gradeCode) },
    { id: "size", label: detailLabel("grade-size"), value: text(sequence.gradeSizeCode) },
    { id: "actual-size", label: detailLabel("actual-size"), value: text(sequence.actualSize) },
    { id: "stock-pile", label: detailLabel("stock-pile"), value: text(sequence.stockPileCode) },
    {
      id: "shipper",
      label: detailLabel("shipper"),
      value: sequence.shipperCompanyCode ? (
        <button
          type="button"
          className="underline"
          onClick={() => onOpenShipper(sequence.shipperCompanyCode as string)}
        >
          {sequence.shipperCompanyCode}
        </button>
      ) : (
        "-"
      ),
    },
    { id: "cargo-code", label: detailLabel("cargo-code"), value: text(sequence.cargoCode) },
    has("loaderCode")
      ? { id: "loader", label: executionLabel("loader"), value: text(sequence.loaderCode) }
      : null,
    has("hopperCode")
      ? { id: "hopper", label: executionLabel("hopper"), value: text(sequence.hopperCode) }
      : null,
    has("transporterCompanyCode")
      ? {
          id: "transporter",
          label: executionLabel("transporter"),
          value: transporterText(sequence),
        }
      : null,
    has("ats")
      ? { id: "ats", label: t(`${L}.grid.ats`), value: formatInstant(sequence.ats) }
      : null,
    has("atc")
      ? { id: "atc", label: t(`${L}.grid.atc`), value: formatInstant(sequence.atc) }
      : null,
  ];

  return (
    <Box
      data-testid="sequence-details-card"
      sx={{
        border: "1px solid var(--stroke-secondary-stroke, #E6EDF2)",
        borderRadius: 1,
        p: 2,
      }}
    >
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
        {t(`${L}.sequence-page.card-title`)}
      </Typography>
      <Box
        sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" }, columnGap: 6 }}
      >
        {fields.flatMap((field) =>
          field
            ? [
                <Box
                  key={field.id}
                  sx={field.span ? { gridColumn: "1 / -1", width: "calc(50% - 24px)" } : undefined}
                >
                  <InfoRow label={field.label}>
                    <InfoValue testId={`sequence-details-${field.id}`}>{field.value}</InfoValue>
                  </InfoRow>
                </Box>,
              ]
            : [],
        )}
      </Box>
    </Box>
  );
};

export default SequenceDetailsCard;
